import React, { useState } from 'react';
import { backwardTrace } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { Search, Loader2, Rewind, ArrowDown, FlaskConical, Droplets, PackageOpen, Truck, ShoppingCart, User, Cylinder, Factory, Boxes } from 'lucide-react';

const SEARCH_TYPES = [
  { value: 'cheese_batch_number', label: 'Cheese batch number', placeholder: 'e.g. CB-2026-014' },
  { value: 'sales_order_number', label: 'Sales order number', placeholder: 'e.g. SO-2026-058' },
  { value: 'dispatch_ref', label: 'Dispatch reference', placeholder: 'e.g. DEM20260910485 or DSP-001' },
  { value: 'make_day_ref', label: 'Make Day reference', placeholder: 'e.g. MD-2026-00001' },
  { value: 'milk_intake_ref', label: 'Milk intake reference', placeholder: 'e.g. MI-2026-00001' },
  { value: 'supplier_lot', label: 'Supplier lot number', placeholder: 'e.g. MA4001-26A007' },
];

export default function BackwardTrace() {
  const [searchType, setSearchType] = useState('cheese_batch_number');
  const [value, setValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    if (!value.trim()) { toast.error('Enter a value to search'); return; }
    setLoading(true);
    try {
      const r = await backwardTrace({ [searchType]: value.trim() });
      setResult(r.data);
      if (!r.data.cheese_batches?.length && !r.data.dispatches?.length) {
        toast.info('No matching trace chain. Try a different value.');
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Trace failed'); }
    finally { setLoading(false); }
  };

  const cur = SEARCH_TYPES.find(t => t.value === searchType);

  return (
    <div className="space-y-4" data-testid="backward-trace-page">
      <div>
        <h1 className="text-xl font-semibold flex items-center gap-2"><Rewind className="w-5 h-5" />Backward Trace</h1>
        <p className="text-sm text-muted-foreground">Start anywhere downstream and walk back to the supplier lot. Sales order → dispatch → cheese batch → make day → milk intake → supplier.</p>
      </div>

      <Card><CardContent className="pt-4">
        <div className="grid md:grid-cols-3 gap-3">
          <div>
            <Label>Search by</Label>
            <Select value={searchType} onValueChange={setSearchType}>
              <SelectTrigger data-testid="trace-search-type"><SelectValue /></SelectTrigger>
              <SelectContent>{SEARCH_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2 flex gap-2">
            <div className="flex-1">
              <Label>Value</Label>
              <Input value={value} onChange={e => setValue(e.target.value)} placeholder={cur?.placeholder} onKeyDown={e => e.key === 'Enter' && run()} data-testid="trace-value-input" />
            </div>
            <Button onClick={run} disabled={loading} className="self-end" data-testid="btn-run-trace">
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Search className="w-4 h-4 mr-1" />}Trace
            </Button>
          </div>
        </div>
      </CardContent></Card>

      {result && <Card data-testid="trace-result"><CardContent className="pt-6">
        <div className="text-sm text-muted-foreground mb-1">Starting point</div>
        <div className="text-xl font-semibold mb-6">{result.starting || '—'}</div>

        <div className="relative pl-8">
          {/* Vertical line */}
          <div className="absolute left-3 top-2 bottom-2 w-0.5 bg-zinc-200" />

          {/* Customer */}
          {result.customer && <TraceNode icon={User} title="Customer" testid="trace-node-customer">
            <div className="font-medium">{result.customer.name}</div>
            {result.customer.delivery_city && <div className="text-xs text-muted-foreground">{result.customer.delivery_city}, {result.customer.delivery_country}</div>}
          </TraceNode>}

          {/* Sales orders */}
          {result.sales_orders?.length > 0 && <TraceNode icon={ShoppingCart} title="Sales orders" testid="trace-node-sales-orders">
            {result.sales_orders.map((so, i) => (
              <div key={i} className="text-sm">
                <span className="font-mono">{so.order_number || so.id}</span>
                {so.order_date && <span className="text-muted-foreground ml-2">{so.order_date}</span>}
              </div>
            ))}
          </TraceNode>}

          {/* Dispatches */}
          {result.dispatches?.length > 0 && <TraceNode icon={Truck} title="Dispatches" testid="trace-node-dispatches">
            {result.dispatches.map((d, i) => (
              <div key={i} className="text-sm">
                <span className="font-mono">{d.dispatch_number || d.reference || d.dispatch_ref || d.id?.slice(0, 8)}</span>
                {d.dispatch_date && <span className="text-muted-foreground ml-2">{d.dispatch_date}</span>}
              </div>
            ))}
          </TraceNode>}

          {/* Cheese batches with make-day */}
          {result.cheese_batches?.length > 0 && result.cheese_batches.map((bn, i) => {
            const b = bn.batch; const md = bn.make_day;
            return (
              <React.Fragment key={i}>
                <TraceNode icon={Boxes} title="Cheese batch" testid={`trace-node-batch-${i}`}>
                  <div className="font-mono font-semibold">{b.batch_number || b.batch_code}</div>
                  <div className="text-xs text-muted-foreground">Use-by {b.use_by_date || '—'} · Qty {b.quantity || '—'}</div>
                </TraceNode>

                {md && <TraceNode icon={FlaskConical} title="Make Day" testid={`trace-node-makeday-${i}`} accent>
                  <div className="font-mono font-semibold">{md.make_day_ref}</div>
                  <div className="text-xs text-muted-foreground">{md.product_type} · {md.make_date} · Supervisor: {md.supervisor || '—'}</div>
                </TraceNode>}

                {md?.milk_used?.length > 0 && <TraceNode icon={Droplets} title="Milk intakes used" testid={`trace-node-milk-${i}`}>
                  <table className="w-full text-xs">
                    <tbody>{md.milk_used.map((mu, j) => (
                      <tr key={j} className="border-b last:border-b-0">
                        <td className="py-1.5 pr-3"><span className="font-mono">{mu.intake_ref || '—'}</span></td>
                        <td className="py-1.5 pr-3 text-muted-foreground"><Cylinder className="inline w-3 h-3 mr-1" />{mu.silo_name || '—'}</td>
                        <td className="py-1.5 pr-3 text-muted-foreground"><Factory className="inline w-3 h-3 mr-1" />{mu.supplier_name || '—'}</td>
                        <td className="py-1.5 text-right font-medium">{mu.quantity_litres?.toLocaleString()}L</td>
                      </tr>))}
                    </tbody>
                  </table>
                </TraceNode>}

                {md?.ingredients_used?.length > 0 && <TraceNode icon={PackageOpen} title="Ingredient lots used" testid={`trace-node-ing-${i}`}>
                  <table className="w-full text-xs">
                    <tbody>{md.ingredients_used.map((iu, j) => (
                      <tr key={j} className="border-b last:border-b-0">
                        <td className="py-1.5 pr-3">{iu.internal_item}</td>
                        <td className="py-1.5 pr-3 text-muted-foreground">lot <span className="font-mono">{iu.supplier_lot || '—'}</span></td>
                        <td className="py-1.5 pr-3 text-muted-foreground"><Factory className="inline w-3 h-3 mr-1" />{iu.supplier_name || '—'}</td>
                        <td className="py-1.5 text-right">{iu.quantity} {iu.unit}</td>
                      </tr>))}
                    </tbody>
                  </table>
                </TraceNode>}

                {md?.packaging_used?.length > 0 && <TraceNode icon={PackageOpen} title="Packaging lots used" testid={`trace-node-pkg-${i}`}>
                  <table className="w-full text-xs">
                    <tbody>{md.packaging_used.map((pu, j) => (
                      <tr key={j} className="border-b last:border-b-0">
                        <td className="py-1.5 pr-3">{pu.internal_item}</td>
                        <td className="py-1.5 pr-3 text-muted-foreground">lot <span className="font-mono">{pu.supplier_lot || '—'}</span></td>
                        <td className="py-1.5 pr-3 text-muted-foreground"><Factory className="inline w-3 h-3 mr-1" />{pu.supplier_name || '—'}</td>
                        <td className="py-1.5 text-right">{pu.quantity} {pu.unit}</td>
                      </tr>))}
                    </tbody>
                  </table>
                </TraceNode>}
              </React.Fragment>
            );
          })}

          {(!result.cheese_batches?.length && !result.dispatches?.length) &&
            <div className="ml-2 text-sm text-muted-foreground italic py-8">No chain found for this value. Check the reference or try a different search type.</div>}
        </div>
      </CardContent></Card>}
    </div>
  );
}

function Boxes_fallback({ className }) {
  // unused — kept to satisfy any earlier reference; real `Boxes` comes from lucide
  return null;
}

function TraceNode({ icon: Icon, title, children, testid, accent }) {
  return (
    <div className="relative pb-5" data-testid={testid}>
      <div className={`absolute -left-6 top-1 w-5 h-5 rounded-full flex items-center justify-center ${accent ? 'bg-pink-600 text-white' : 'bg-white border-2 border-zinc-300 text-zinc-600'}`}>
        <Icon className="w-3 h-3" />
      </div>
      <div className={`rounded-md p-3 ${accent ? 'bg-pink-50 border border-pink-200' : 'bg-zinc-50 border border-zinc-200'}`}>
        <div className="text-xs font-semibold uppercase tracking-wider text-zinc-500 mb-1.5">{title}</div>
        {children}
      </div>
      <ArrowDown className="absolute -left-[14px] top-7 w-3 h-3 text-zinc-300" />
    </div>
  );
}
